import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFacebook, faTwitter, faInstagram, faLinkedin, faGooglePlay } from '@fortawesome/free-brands-svg-icons';
import { faEnvelope, faPhone, faMapMarkerAlt, faClock, faChevronDown, faChevronUp, faShieldAlt, faTruck, faExchangeAlt, faFileContract, faUserShield, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { FaInfoCircle } from 'react-icons/fa';

const Footer = () => {
	const [isMobile, setIsMobile] = useState(false);
	const [openSection, setOpenSection] = useState(null);

	useEffect(() => {
		const checkScreenSize = () => {
			setIsMobile(window.innerWidth < 768);
		};

		checkScreenSize();
		window.addEventListener('resize', checkScreenSize);

		return () => window.removeEventListener('resize', checkScreenSize);
	}, []);

	const toggleSection = (section) => {
		setOpenSection(openSection === section ? null : section);
	};

	// Mobile Footer
	if (isMobile) {
		return (
			<footer className="bg-[#1A1614] text-[#FDFBF7] relative overflow-hidden">
				{/* Subtle wood texture overlay for the footer background */}
				<div className="absolute inset-0 opacity-5 pointer-events-none bg-[url('https://www.transparenttextures.com/patterns/wood-pattern.png')]"></div>

				<div className="container mx-auto px-6 py-12 relative z-10">
					{/* Company Info */}
					<div className="mb-12 text-center">
						<h3 className="text-3xl font-serif font-bold mb-4 tracking-tight text-[#FDFBF7]">
							Zakkuu<span className="text-[#A38A64]">.</span>
						</h3>
						<p className="text-stone-400 mb-8 max-w-sm mx-auto text-sm font-serif italic leading-relaxed">
							"Crafting heritage, designing comfort. Premium furniture for the modern artisan home."
						</p>

						{/* Social Icons - Circular Wood Style */}
						<div className="flex justify-center space-x-4 mb-10">
							{[
								{ icon: faFacebook, link: "https://www.facebook.com/people/Zakkuu/61586289465230/?rdid=os2gzm5852a8vPtc&share_url=https%3A%2F%2Fwww.facebook.com%2Fshare%2F1BfdGv9bG7%2F" },
								{ icon: faInstagram, link: "https://www.instagram.com/zakkuu_del?igsh=MXJlYTV0cDExMWllbQ==" },
								{ icon: faTwitter, link: "#" },
								{ icon: faLinkedin, link: "#" }
							].map((social, i) => (
								<a
									key={i}
									href={social.link}
									target="_blank"
									rel="noopener noreferrer"
									className="w-10 h-10 flex items-center justify-center border border-stone-700 text-stone-300 hover:border-[#A38A64] hover:text-[#A38A64] transition-all duration-300"
								>
									<FontAwesomeIcon icon={social.icon} />
								</a>
							))}
						</div>

						{/* Google Play - Artisan Variant */}
						<div className="flex justify-center mb-10">
							<a
								href="https://play.google.com/store/apps/details?id=zakkuu.com"
								target="_blank"
								rel="noopener noreferrer"
								className="inline-flex items-center bg-transparent border border-stone-600 text-stone-200 px-5 py-2 hover:bg-stone-800 transition-all duration-300 group"
							>
								<FontAwesomeIcon icon={faGooglePlay} className="text-2xl mr-3 group-hover:text-[#A38A64]" />
								<div className="flex flex-col items-start leading-none">
									<span className="text-[9px] uppercase tracking-widest font-bold">Available on</span>
									<span className="text-lg font-serif font-bold">Google Play</span>
								</div>
							</a>
						</div>
					</div>

					{/* Contact Info Accordion - Wood & Bone Style */}
					<div className="mb-4">
						<button
							className={`w-full flex justify-between items-center py-4 px-5 text-left transition-colors duration-300 ${openSection === 'contact' ? 'bg-[#7A5C33] text-white' : 'bg-stone-800/40 text-stone-300'}`}
							onClick={() => toggleSection('contact')}
						>
							<div className="flex items-center space-x-3">
								<FontAwesomeIcon icon={faPhone} className={openSection === 'contact' ? 'text-stone-200' : 'text-[#A38A64]'} />
								<h4 className="text-sm font-bold uppercase tracking-widest">Connect With Us</h4>
							</div>
							<FontAwesomeIcon icon={openSection === 'contact' ? faChevronUp : faChevronDown} size="xs" />
						</button>
						{openSection === 'contact' && (
							<div className="bg-stone-900/50 p-6 space-y-6 border-x border-stone-800">
								<div className="flex items-start space-x-4">
									<div className="text-[#A38A64] mt-1"><FontAwesomeIcon icon={faPhone} /></div>
									<div>
										<h5 className="text-[#FDFBF7] font-serif font-bold">Call the Studio</h5>
										<p className="text-stone-400 text-xs mt-1">+91-9220956556</p>
									</div>
								</div>

								<div className="flex items-start space-x-4">
									<div className="text-[#A38A64] mt-1"><FontAwesomeIcon icon={faEnvelope} /></div>
									<div>
										<h5 className="text-[#FDFBF7] font-serif font-bold">Write to Us</h5>
										<p className="text-stone-400 text-xs mt-1">support@zakkuu.com</p>
									</div>
								</div>

								<div className="flex items-start space-x-4">
									<div className="text-[#A38A64] mt-1"><FontAwesomeIcon icon={faMapMarkerAlt} /></div>
									<div>
										<h5 className="text-[#FDFBF7] font-serif font-bold">Headquarters</h5>
										<p className="text-stone-400 text-xs mt-1 leading-relaxed">
											D-233, D Block, Sector 63, Noida,<br /> Uttar Pradesh 201309
										</p>
									</div>
								</div>
							</div>
						)}
					</div>

					{/* Policies Accordion */}
					<div className="mb-10">
						<button
							className={`w-full flex justify-between items-center py-4 px-5 text-left transition-colors duration-300 ${openSection === 'policies' ? 'bg-[#7A5C33] text-white' : 'bg-stone-800/40 text-stone-300'}`}
							onClick={() => toggleSection('policies')}
						>
							<div className="flex items-center space-x-3">
								<FontAwesomeIcon icon={faShieldAlt} className={openSection === 'policies' ? 'text-stone-200' : 'text-[#A38A64]'} />
								<h4 className="text-sm font-bold uppercase tracking-widest">Company Policies</h4>
							</div>
							<FontAwesomeIcon icon={openSection === 'policies' ? faChevronUp : faChevronDown} size="xs" />
						</button>
						{openSection === 'policies' && (
							<div className="bg-stone-900/50 border-x border-stone-800">
								<ul className="divide-y divide-stone-800">
									{['About Us', 'Privacy Policy', 'Terms Conditions', 'Refund Policy', 'Shipping Policy', 'Return Policy'].map((policy, i) => (
										<li key={i}>
											<Link onClick={() => {
												window.scrollTo({
													top: 0,
													left: 0,
													behavior: "smooth",
												});
											}} to={`/${policy.toLowerCase().replace(/ /g, '-')}`} className="block p-4 text-stone-400 hover:text-[#A38A64] text-sm font-serif">
												{policy}
											</Link>
										</li>
									))}
								</ul>
							</div>
						)}
					</div>

					{/* Primary Action Button */}
					{/* <div className="mb-12">
						<Link
							to="/contact-us"
							className="block w-full text-center bg-[#7A5C33] text-[#FDFBF7] py-4 font-bold text-xs uppercase tracking-[0.3em] hover:bg-[#8B6B42] transition-colors shadow-xl"
						>
							Inquire Today
						</Link>
					</div> */}

					{/* Join as Seller CTA */}
					<div className="mb-12">
						<Link
							to="/become-seller"
							onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
							className="block w-full text-center bg-[#A38A64] text-[#1A1614] py-4 font-bold text-xs uppercase tracking-[0.3em] hover:bg-[#8B734F] transition-all duration-300 shadow-xl"
						>
							Join as a Seller
						</Link>
						<p className="text-stone-500 text-[11px] text-center mt-3 font-serif italic">
							Partner with us & showcase your craftsmanship.
						</p>
					</div>

					{/* Bottom Section */}
					<div className="pt-8 border-t border-stone-800 flex flex-col items-center space-y-4">
						{/* Main Copyright */}
						<p className="text-stone-500 text-[10px] uppercase tracking-[0.2em] font-medium">
							&copy; {new Date().getFullYear()} Zakkuu Furniture. Handcrafted Excellence.
						</p>

						{/* Parent Company Mark */}
						<div className="flex items-center justify-center group">
							<span className="w-6 h-[1px] bg-stone-800 group-hover:bg-[#A38A64] transition-colors duration-500"></span>
							<span className="mx-4 text-stone-600 text-[11px] font-serif italic tracking-wide">
								A Unit of <span className="text-stone-400 group-hover:text-[#FDFBF7] transition-colors duration-500">Digital Night Owl</span>
							</span>
							<span className="w-6 h-[1px] bg-stone-800 group-hover:bg-[#A38A64] transition-colors duration-500"></span>
						</div>
					</div>
				</div>
			</footer>
		);
	}

	// Desktop Footer
	return (
		<footer className="bg-[#FDFBF7] text-[#4A443C] relative overflow-hidden border-t border-stone-200">
			{/* Subtle light wood grain texture overlay */}
			<div className="absolute inset-0 opacity-[0.05] pointer-events-none bg-[url('https://www.transparenttextures.com/patterns/pinstriped-suit.png')]"></div>

			<div className="container mx-auto px-4 py-16 relative z-10">
				<div className="grid grid-cols-1 lg:grid-cols-4 gap-12">

					{/* Company Info */}
					<div className="lg:col-span-1">
						<div className="mb-8">
							{/* Rich Wood-tone for the Logo */}
							<h3 className="text-4xl font-serif font-bold mb-4 text-[#7A5C33] tracking-tight">
								Zakkuu<span className="text-sm align-top font-sans text-stone-400">™</span>
							</h3>
							<p className="text-stone-500 mb-6 text-sm leading-relaxed italic font-serif">
								Transforming spaces with premium furniture that combines heritage craftsmanship, comfort, and functionality for modern living.
							</p>
						</div>

						<div className="mb-8">
							<h4 className="text-[#A38A64] font-bold mb-5 text-[10px] uppercase tracking-[0.2em]">Follow Our Journey</h4>
							<div className="flex space-x-4">
								{[
									{
										icon: faFacebook,
										link: "https://www.facebook.com/people/Zakkuu/61586289465230/?rdid=os2gzm5852a8vPtc&share_url=https%3A%2F%2Fwww.facebook.com%2Fshare%2F1BfdGv9bG7%2F",
										label: "Facebook",
									},
									{
										icon: faInstagram,
										link: "https://www.instagram.com/zakkuu_del?igsh=MXJlYTV0cDExMWllbQ==",
										label: "Instagram",
									},
									{
										icon: faTwitter,
										link: "#",
										label: "Twitter",
									},
									{
										icon: faLinkedin,
										link: "#",
										label: "LinkedIn",
									},
								].map((item, i) => (
									<a
										key={i}
										href={item.link}
										target="_blank"
										rel="noopener noreferrer"
										aria-label={item.label}
										className="border border-stone-200 hover:border-[#7A5C33] text-[#7A5C33] p-3 rounded-none transition-all duration-500 transform hover:-translate-y-1 bg-white shadow-sm"
									>
										<FontAwesomeIcon icon={item.icon} size="sm" />
									</a>
								))}
							</div>
						</div>

						{/* Mobile App Link - Refined Light Version */}
						<div className="mb-8">
							<a
								href="https://play.google.com/store/apps/details?id=zakkuu.com"
								className="inline-flex items-center bg-white border border-stone-200 text-stone-800 px-5 py-2 hover:border-[#7A5C33] transition-all duration-500 group shadow-sm"
							>
								<FontAwesomeIcon icon={faGooglePlay} className="text-xl mr-3 text-[#A38A64]" />
								<div className="flex flex-col items-start leading-none">
									<span className="text-[9px] uppercase tracking-widest text-stone-400">Experience on</span>
									<span className="text-lg font-serif group-hover:text-[#7A5C33] transition-colors">Google Play</span>
								</div>
							</a>
						</div>

						<div className="border-l-2 border-[#A38A64]/30 pl-4 py-1">
							<p className="text-stone-400 text-xs italic font-serif">
								"Crafting comfort, designing dreams since 2010."
							</p>
						</div>
					</div>

					{/* Policies */}
					<div className="lg:col-span-1">
						<div className="flex items-center mb-8">
							<h4 className="text-xl font-serif font-bold text-stone-800">House Policies</h4>
						</div>

						<ul className="space-y-1">
							{[
								{ to: "/about-us", label: "About Us", icon: faInfoCircle },
								{ to: "/privacy-policy", label: "Privacy Policy", icon: faUserShield },
								{ to: "/terms-conditions", label: "Terms & Conditions", icon: faFileContract },
								{ to: "/refund-policy", label: "Refund Policy", icon: faExchangeAlt },
								{ to: "/shipping-policy", label: "Shipping Policy", icon: faTruck },
								{ to: "/return-policy", label: "Return Policy", icon: faExchangeAlt }
							].map((link, idx) => (
								<li key={idx} className="group">
									<Link onClick={() => {
										window.scrollTo({
											top: 0,
											left: 0,
											behavior: "smooth",
										});
									}} to={link.to} className="flex items-center py-2 group-hover:translate-x-2 transition-transform duration-300">
										<FontAwesomeIcon icon={link.icon} className="text-stone-300 mr-3 text-xs group-hover:text-[#A38A64]" />
										<span className="text-stone-600 group-hover:text-stone-900 text-sm font-serif">{link.label}</span>
									</Link>
								</li>
							))}
						</ul>
					</div>

					{/* Contact Info */}
					<div className="lg:col-span-2">
						<h4 className="text-xl font-serif font-bold text-stone-800 mb-8">Contact Our Studio</h4>

						<div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
							{/* Phone Block */}
							<div className="border border-stone-100 p-6 hover:border-[#A38A64]/30 transition-colors bg-white shadow-sm">
								<div className="flex items-center mb-4">
									<FontAwesomeIcon icon={faPhone} className="text-[#A38A64] mr-3" />
									<h5 className="text-stone-400 font-bold text-[10px] uppercase tracking-widest">Call to Us</h5>
								</div>
								<p className="text-stone-400 text-[10px] mb-2 uppercase">Monday – Sunday / 24/7</p>
								<span className="text-stone-800 text-lg font-serif">+91 92209 56556</span>
							</div>

							{/* Email Block */}
							<div className="border border-stone-100 p-6 hover:border-[#A38A64]/30 transition-colors bg-white shadow-sm">
								<div className="flex items-center mb-4">
									<FontAwesomeIcon icon={faEnvelope} className="text-[#A38A64] mr-3" />
									<h5 className="text-stone-400 font-bold text-[10px] uppercase tracking-widest">Write to Us</h5>
								</div>
								<p className="text-stone-400 text-[10px] mb-2 uppercase">Response within 24 Hours</p>
								<span className="text-stone-800 text-lg font-serif">support@zakkuu.com</span>
							</div>
						</div>

						{/* Headquarters */}
						<div className="border border-stone-100 p-6 bg-white shadow-sm">
							<div className="flex items-start mb-4">
								<FontAwesomeIcon icon={faMapMarkerAlt} className="text-[#A38A64] mt-1 mr-4" />
								<div>
									<h5 className="text-stone-400 font-bold text-[10px] uppercase tracking-widest mb-3">Headquarters</h5>
									<p className="text-stone-600 text-sm font-serif leading-relaxed mb-4">
										D-233, D Block, Sector 63, Noida,<br /> Uttar Pradesh 201309
									</p>
									<div className="flex items-start text-[11px] text-stone-400 font-serif italic">
										<FontAwesomeIcon icon={faClock} className="mr-2 mt-0.5" />
										<span>
											Weekdays: 9:00 AM – 8:00 PM | Saturday: 11:00 AM – 3:00 PM
										</span>
									</div>
								</div>
							</div>
						</div>

						{/* <div className="mt-8">
							<Link
								to="/contact-us"
								className="inline-flex items-center justify-center bg-[#7A5C33] text-white py-4 px-10 rounded-none transition-all duration-500 hover:bg-stone-800 font-bold text-xs uppercase tracking-[0.2em] shadow-md hover:shadow-xl"
							>
								Inquire Today
							</Link>
						</div> */}
					</div>
				</div>

				{/* Seller CTA Section */}
				<div className="mt-20 mb-10 bg-[#7A5C33] text-white py-12 px-10 flex flex-col md:flex-row items-center justify-between shadow-xl">
					<div className="mb-6 md:mb-0">
						<h3 className="text-2xl font-serif font-bold mb-2">
							Grow Your Craft With Zakkuu
						</h3>
						<p className="text-stone-200 text-sm font-serif italic">
							Join our artisan marketplace and reach customers across India.
						</p>
					</div>

					<Link
						to="/become-seller"
						onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
						className="bg-white text-[#7A5C33] px-8 py-4 font-bold text-xs uppercase tracking-[0.3em] hover:bg-stone-100 transition-all duration-300 shadow-md"
					>
						Join as a Seller
					</Link>
				</div>

				{/* Bottom Section */}
				<div className="border-t border-stone-200 mt-16 pt-8">
					<div className="flex flex-col md:flex-row justify-between items-center font-serif text-[11px] tracking-widest uppercase text-stone-400">
						<div className="mb-4 md:mb-0">
							&copy; {new Date().getFullYear()} Zakkuu Furniture. All Rights Reserved.
						</div>
						<div className="flex items-center">
							<span className="w-8 h-[1px] bg-stone-200 mr-4"></span>
							Unit of Digital Night Owl
						</div>
					</div>
				</div>
			</div>
		</footer>
	);
};

export default Footer;
